import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/transfert/chauffeur-transfert.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const saintRaphaelEn: ChauffeurPage = {
  slug: "saint-raphael",
  title: "VTC & private chauffeur in Saint-Raphaël",
  subtitle:
    "Private chauffeur in Saint-Raphaël: Nice airport transfers (~1 h), TGV station shuttles and chauffeur hire between the port and the Estérel, in the Var, 7 days a week.",
  keywords: [
    "vtc saint-raphaël",
    "private chauffeur saint-raphael",
    "saint-raphael esterel chauffeur",
    "saint-raphael nice airport transfer",
  ],
  hero: {
    title: "VTC & private chauffeur in Saint-Raphaël",
    subtitle:
      "Estérel seaside resort and the golfe's TGV gateway: Nice airport transfers (~1 h), station shuttles and chauffeur hire, 24/7.",
    keywords: [
      "VTC Saint-Raphaël",
      "Estérel chauffeur",
      "Saint-Raphaël TGV station",
      "Nice airport transfer",
    ],
    imageSrc: heroImage.src,
    imageAlt:
      "Private chauffeur & VTC in Saint-Raphaël, Estérel seaside resort",
    badges: [
      "Nice airport ~1 h",
      "Saint-Raphaël TGV station",
      "Bilingual chauffeurs",
    ],
  },
  benefits: {
    heading: "Why a chauffeur in Saint-Raphaël",
    items: [
      {
        title: "The golfe's TGV gateway",
        description:
          "Saint-Raphaël-Valescure station links Paris and Marseille by TGV: we handle your station ⇄ hotel shuttles and connections across the Var.",
      },
      {
        title: "Nice airport with no driving",
        description:
          "Around 1 h via the A8 to Nice Côte d'Azur airport, flight tracking and welcome included — further than Cannes, so we build in the margin.",
      },
      {
        title: "The Estérel and its coves",
        description:
          "Corniche d'Or, Cap Dramont, Agay, Anthéor: the chauffeur knows the red-rock road of the massif and its viewpoints.",
      },
      {
        title: "Ports & yachting",
        description:
          "Vieux-Port and Port Santa Lucia, one of the coast's largest: crew and guest links, rated 5.0/5 from 38 Google reviews.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Saint-Raphaël",
    services: [
      {
        title: "Nice airport transfers",
        text: "Saint-Raphaël ⇄ Nice Côte d'Azur airport in ~1 h via the A8, with flight tracking and an anticipated margin.",
      },
      {
        title: "TGV station shuttle",
        text: "Pick-up at Saint-Raphaël-Valescure station and a link to your hotel, a villa or another town on the golfe.",
      },
      {
        title: "Chauffeur hire",
        text: "By the hour or by the day along the Corniche d'Or, between beaches, port and hinterland.",
      },
      {
        title: "Estérel & coves",
        text: "Cap Dramont, the Débarquement beach, Agay and the Île d'Or: drop-off at trailheads and diving clubs.",
      },
      {
        title: "Long distance from Cannes/Nice",
        text: "Journeys from Cannes, Nice or the airport to Saint-Raphaël and the golfe, by saloon, van or VIP minibus.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Vieux-Port & seafront",
        description:
          "Casino, Notre-Dame-de-la-Victoire basilica and promenade.",
      },
      {
        name: "Saint-Raphaël-Valescure station",
        description:
          "TGV station in the heart of town, connections across the golfe.",
      },
      {
        name: "Port Santa Lucia",
        description: "Large marina, departure for sea outings.",
      },
      {
        name: "Corniche d'Or & Cap Dramont",
        description: "The Estérel's red-rock road towards Agay and Anthéor.",
      },
      {
        name: "Valescure",
        description: "Residential district and golf, away from the seafront.",
      },
      {
        name: "Nice airport",
        description: "Terminals 1 and 2, ~1 h via the A8.",
      },
    ],
  },
  generic: {
    heading: "A chauffeur at the heart of the Estérel",
    subheading:
      "Seaside resort, TGV station and red massif: Saint-Raphaël is the gateway to the golfe.",
    paragraphs: [
      "Backed by the Estérel massif and facing the sea, Saint-Raphaël is the gateway to the golfe de Fréjus: a seaside resort, a large marina and, above all, a TGV station linking Paris and Marseille. KS Rent Services provides transfers, station shuttles and chauffeur hire here all year round.",
      "Nice Côte d'Azur airport is around an hour away via the A8: further than from Cannes, this journey calls for a built-in margin, which we always include with flight tracking. Saint-Raphaël-Valescure station also makes a handy transfer point for reaching the rest of the Var.",
      "On the ground, the chauffeur follows the Corniche d'Or along the red porphyry — Cap Dramont, the Débarquement beach, Agay, Anthéor — dropping you at the coves, diving clubs or trailheads, then returning at the agreed time.",
    ],
    bullets: [
      "Nice airport in ~1 h",
      "Saint-Raphaël TGV station shuttle",
      "Corniche d'Or & Estérel coves",
    ],
  },
  process: {
    heading: "Book a chauffeur in Saint-Raphaël",
    steps: [
      "Tell us your route (airport, TGV station, port, Estérel) and your times.",
      "We confirm the vehicle, the margin for Nice airport and the fixed package.",
      "The chauffeur tracks your flight or train and greets you on arrival.",
      "Chauffeur hire or drop-off along the Corniche d'Or, pick-up on request.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Saint-Raphaël"),
    {
      q: "How long to Nice airport?",
      a: "Around 1 hour via the A8. As Saint-Raphaël lies west of Nice, this transfer is longer than from Cannes: we allow a margin and track your flight.",
    },
    {
      q: "Do you offer shuttles from the TGV station?",
      a: "Yes. We pick up at Saint-Raphaël-Valescure station and link you to your hotel, a villa or another town on the golfe and across the Var.",
    },
    {
      q: "Do you serve the Estérel and the Agay coves?",
      a: "Yes, the Corniche d'Or, Cap Dramont, Agay and Anthéor are part of our routes, with drop-off at beaches, diving clubs and trailheads.",
    },
    {
      q: "How far in advance should I book?",
      a: "Book as early as possible in summer and over long weekends; we also handle last-minute requests subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English, used to an international clientele.",
    },
    {
      q: "Which vehicles do you offer?",
      a: "Mercedes E-Class and S-Class saloons, 7-seat vans, SUVs and VIP minibuses, depending on passengers and luggage.",
    },
  ],
  relatedLinks: [
    {
      label: "Saint-Tropez chauffeur rates",
      href: "/en/chauffeur/saint-tropez",
    },
    { label: "Chauffeur service Cannes", href: "/en/chauffeur/cannes" },
    {
      label: "Transfers across the French Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    {
      label: "Rent a car Saint-Raphaël",
      href: "/en/location/car-rental-saint-raphael",
    },
  ],
  cta: {
    title: "Your private chauffeur in Saint-Raphaël",
    text: "Nice airport transfer, TGV station shuttle or chauffeur hire in the Estérel: fast reply, 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Car service Saint-Raphaël — private chauffeur & transfers",
    description:
      "Private car service in Saint-Raphaël: Nice airport transfers (~1 h), TGV station shuttles, Corniche d'Or and the Estérel. 5.0/5 from 38 reviews.",
    canonical: "/en/chauffeur/saint-raphael",
  },
};

export default saintRaphaelEn;
